// The Mediterranean: painted sea shader and the open water around the island.
import * as THREE from 'three';
import { PI } from '../lib/math.js';
import { add } from '../lib/factory.js';
import { rippleTex } from '../textures/ground.js';

export let seaNormal, waveTime;

export function buildSea() {
  // the Mediterranean all around, fading into the haze
  seaNormal = rippleTex.clone();
  seaNormal.repeat.set(220, 220);
  seaNormal.needsUpdate = true;
  waveTime = { value: 0 };
  // Storybook (BotW-like) sea, shared by the open sea and the wave plane off the beach: soft painted depth bands
  // measured from the island's coastline, a caustic net in the shallows, crescent wave strokes that appear and
  // fade, lacy surf at the shore, and stretched sun glints. Everything is in world xz (vW), time in seconds.
  const SEA_GLSL = `
  float sHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float sNoise(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(sHash(i), sHash(i + vec2(1, 0)), f.x), mix(sHash(i + vec2(0, 1)), sHash(i + vec2(1, 1)), f.x), f.y); }
  // signed distance to the island outline (beach edge at z = 63)
  float islandD(vec2 w) { vec2 q = abs(w - vec2(0.0, -1.0)) - vec2(51.0, 50.0); return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - 14.0; }
  vec3 seaColor(vec2 w, float t, out float foamA) {
    float d = islandD(w), dd = d + (sNoise(w * .07) - .5) * 7.0;
    vec3 c = vec3(.4, .93, .84);                                   // sunlit shallows
    c = mix(c, vec3(.16, .7, .76), smoothstep(3.0, 9.0, dd));      // turquoise
    c = mix(c, vec3(.07, .43, .67), smoothstep(16.0, 34.0, dd));   // open water
    c = mix(c, vec3(.05, .3, .58), smoothstep(60.0, 160.0, dd));   // deep
    // caustic net over the sand
    float caus = pow(abs(sin(w.x * 1.7 + t * .9 + sin(w.y * 1.3)) * sin(w.y * 1.5 - t * .7 + sin(w.x))), 5.0);
    c += caus * .2 * (1.0 - smoothstep(1.0, 10.0, dd));
    // crescent wave strokes on a jittered grid, each living and fading on its own clock
    vec2 p = w * vec2(.075, .14) + vec2(t * .03, -t * .02); vec2 id = floor(p), f = fract(p);
    float h = sHash(id), life = sin(t * .9 + h * 6.283);
    vec2 g = f - vec2(.3 + .4 * sHash(id + 3.1), .5); float arc = g.y + g.x * g.x * 2.4;
    float aa = fwidth(arc) * 1.2 + .004;
    float stroke = (1.0 - smoothstep(.035, .035 + aa, abs(arc))) * (1.0 - smoothstep(.13, .21, abs(g.x))) * step(.72, h) * smoothstep(.3, .8, life);
    stroke *= smoothstep(6.0, 14.0, d) * (1.0 - smoothstep(120.0, 220.0, d));
    // surf: a bright rim plus lacy bands rolling in toward the coast
    float lace = smoothstep(.0, .2, sin(dd * 1.25 + t * 1.8)) * (1.0 - smoothstep(.8, 5.5, dd)) * smoothstep(.42, .62, sNoise(w * .55 + vec2(t * .25, 0.0)));
    float rim = 1.0 - smoothstep(.0, 1.6, dd);
    // glints: stretched soft highlights drifting with the swell
    float glint = pow(sNoise(w * vec2(.35, .9) + vec2(t * .4, t * .12)), 40.0) * 1.5 * smoothstep(8.0, 30.0, d);
    foamA = clamp(rim * .9 + lace * .75 + stroke * .7, 0.0, 1.0);
    return mix(c, vec3(.97, .99, .98), clamp(foamA + glint, 0.0, 1.0));
  }
`;
  const seaMat = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.55,
    metalness: 0,
    normalMap: seaNormal,
    normalScale: new THREE.Vector2(0.04, 0.04),
    envMapIntensity: 0.5,
  });
  seaMat.onBeforeCompile = (sh) => {
    sh.uniforms.uTime = waveTime;
    sh.vertexShader =
      'varying vec2 vW;\n' +
      sh.vertexShader.replace(
        '#include <begin_vertex>',
        '#include <begin_vertex>\nvW = (modelMatrix * vec4(position, 1.0)).xz;',
      );
    sh.fragmentShader =
      'uniform float uTime; varying vec2 vW;\n' +
      SEA_GLSL +
      sh.fragmentShader.replace(
        '#include <color_fragment>',
        '#include <color_fragment>\nfloat foamA; diffuseColor.rgb = seaColor(vW, uTime, foamA);',
      );
  };
  add(new THREE.CircleGeometry(1400, 64).rotateX(-PI / 2), seaMat, 0, -0.42, 0).castShadow = false;
  {
    const W = 520,
      D = 180,
      oz = 62 + D / 2,
      geo = new THREE.PlaneGeometry(W, D, 260, 110).rotateX(-PI / 2);
    const mat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.5,
      metalness: 0,
      normalMap: seaNormal,
      normalScale: new THREE.Vector2(0.04, 0.04),
      transparent: true,
      envMapIntensity: 0.55,
    });
    mat.onBeforeCompile = (sh) => {
      sh.uniforms.uTime = waveTime;
      const wave = `
      uniform float uTime; varying float vH; varying vec2 vW;
      float fadeW(vec2 w) { return smoothstep(63.0, 67.0, w.y) * (1.0 - smoothstep(190.0, 240.0, w.y)) * (1.0 - smoothstep(180.0, 255.0, abs(w.x))); }
      float waveH(vec2 w) {
        float h = sin(w.y * .55 + w.x * .05 + uTime * 1.6) * .09 + sin(dot(w, vec2(.21, .17)) + uTime * 1.3) * .05
                + sin(dot(w, vec2(-.5, .9)) + uTime * 2.4) * .022 + sin(dot(w, vec2(.9, .6)) * 1.3 + uTime * 3.1) * .012;
        return h * fadeW(w) * (1.0 + smoothstep(78.0, 64.0, w.y) * .6);
      }
`;
      sh.vertexShader =
        wave +
        sh.vertexShader
          .replace(
            '#include <beginnormal_vertex>',
            `vec2 wp = position.xz + vec2(0.0, ${oz.toFixed(1)}); float e = .3;
        float hx = waveH(wp + vec2(e, 0.0)) - waveH(wp - vec2(e, 0.0)), hz = waveH(wp + vec2(0.0, e)) - waveH(wp - vec2(0.0, e));
        vec3 objectNormal = normalize(vec3(-hx / (2.0 * e), 1.0, -hz / (2.0 * e)));`,
          )
          .replace(
            '#include <begin_vertex>',
            '#include <begin_vertex>\nvH = waveH(wp); transformed.y += vH; vW = wp;',
          );
      sh.fragmentShader =
        'uniform float uTime; varying float vH; varying vec2 vW;\n' +
        SEA_GLSL +
        sh.fragmentShader.replace(
          '#include <color_fragment>',
          `#include <color_fragment>
      float foamA; diffuseColor.rgb = seaColor(vW, uTime, foamA);
      float crest = smoothstep(.11, .135, vH) * (1.0 - clamp(islandD(vW) / 40.0, 0.0, 1.0) * .8);
      diffuseColor.rgb = mix(diffuseColor.rgb, vec3(.97, .98, .97), clamp(crest * .5, 0.0, 1.0));
      diffuseColor.a = mix(.78, 1.0, smoothstep(63.0, 68.0, vW.y));`,
        );
    };
    const m = add(geo, mat, 0, -0.33, oz);
    m.castShadow = false;
  }
}
